import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Check, AlertCircle } from 'lucide-react';
import { authenticatedFetch } from '../utils/auth';
import { getApiBaseUrl } from '../utils/config';
import { toast } from 'react-hot-toast';

const SectionSelectionModal = ({ 
  isOpen, 
  onClose, 
  dashboardId, 
  platformLanguage, 
  companyName,
  onDownload 
}) => {
  const { t } = useTranslation();
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSections, setIsFetchingSections] = useState(false);

  // Fetch available sections when modal opens
  useEffect(() => {
    if (isOpen && dashboardId) {
      fetchAvailableSections();
    }
  }, [isOpen, dashboardId, platformLanguage]);

  const fetchAvailableSections = async () => {
    setIsFetchingSections(true);
    try {
      console.log(`[SectionSelectionModal] Fetching sections for dashboard ${dashboardId}, language: ${platformLanguage}`);
      
      const response = await authenticatedFetch(
        `api/dashboards/${dashboardId}/available-sections/${platformLanguage}`,
        { method: 'GET' }
      );

      console.log(`[SectionSelectionModal] Response status: ${response.status}`);

      if (!response.ok) {
        // If the new API is not available, fall back to the old sections endpoint
        if (response.status === 404) {
          console.log('Available sections API not found, falling back to sections endpoint');
          const fallbackResponse = await authenticatedFetch(
            `api/dashboards/${dashboardId}/sections${platformLanguage === 'ja' ? '/japanese' : ''}`,
            { method: 'GET' }
          );
          
          if (!fallbackResponse.ok) {
            console.error(`[SectionSelectionModal] Fallback response failed: ${fallbackResponse.status}`);
            throw new Error('Failed to fetch sections');
          }
          
          const fallbackData = await fallbackResponse.json();
          console.log(`[SectionSelectionModal] Fallback data keys:`, Object.keys(fallbackData));
          
          // Transform the sections data to include selection state
          const sectionsWithState = Object.entries(fallbackData).map(([key, content]) => ({
            key,
            title: getSectionTitle(key),
            content: content || '',
            hasContent: content && content !== '{}' && content.trim() !== ''
          }));

          console.log(`[SectionSelectionModal] Sections with state:`, sectionsWithState.map(s => ({ key: s.key, hasContent: s.hasContent })));

          setAvailableSections(sectionsWithState);
          
          // Pre-select sections that have content
          const sectionsWithContent = sectionsWithState
            .filter(section => section.hasContent)
            .map(section => section.key);
          setSelectedSections(sectionsWithContent);
          
          console.log(`[SectionSelectionModal] Selected sections:`, sectionsWithContent);
          return;
        }
        
        throw new Error('Failed to fetch available sections');
      }

      const responseData = await response.json();
      console.log(`[SectionSelectionModal] Response data:`, responseData);
      
      // Handle the new response format from the backend
      let sectionsWithState;
      
      if (responseData.available_sections && Array.isArray(responseData.available_sections)) {
        // New format: { available_sections: [{ key, title, has_content }] }
        sectionsWithState = responseData.available_sections.map(section => ({
          key: section.key,
          title: section.title || getSectionTitle(section.key),
          content: '', // Content is not provided in this format
          hasContent: section.has_content
        }));
        console.log(`[SectionSelectionModal] New format - sections with state:`, sectionsWithState.map(s => ({ key: s.key, hasContent: s.hasContent })));
      } else {
        // Fallback to old format: { key: content }
        sectionsWithState = Object.entries(responseData).map(([key, content]) => ({
          key,
          title: getSectionTitle(key),
          content: content || '',
          hasContent: content && content !== '{}' && content.trim() !== ''
        }));
        console.log(`[SectionSelectionModal] Old format - sections with state:`, sectionsWithState.map(s => ({ key: s.key, hasContent: s.hasContent })));
      }

      setAvailableSections(sectionsWithState);
      
      // Pre-select sections that have content
      const sectionsWithContent = sectionsWithState
        .filter(section => section.hasContent)
        .map(section => section.key);
      setSelectedSections(sectionsWithContent);
      
      console.log(`[SectionSelectionModal] Final selected sections:`, sectionsWithContent);
      
      // If no sections have content, try the other language as fallback
      if (sectionsWithContent.length === 0) {
        console.log(`[SectionSelectionModal] No sections with content for ${platformLanguage}, trying fallback language`);
        const fallbackLanguage = platformLanguage === 'ja' ? 'en' : 'ja';
        
        try {
          const fallbackResponse = await authenticatedFetch(
            `api/dashboards/${dashboardId}/available-sections/${fallbackLanguage}`,
            { method: 'GET' }
          );
          
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            console.log(`[SectionSelectionModal] Fallback language data:`, fallbackData);
            
            let fallbackSectionsWithState;
            if (fallbackData.available_sections && Array.isArray(fallbackData.available_sections)) {
              fallbackSectionsWithState = fallbackData.available_sections.map(section => ({
                key: section.key,
                title: section.title || getSectionTitle(section.key),
                content: '',
                hasContent: section.has_content
              }));
            } else {
              fallbackSectionsWithState = Object.entries(fallbackData).map(([key, content]) => ({
                key,
                title: getSectionTitle(key),
                content: content || '',
                hasContent: content && content !== '{}' && content.trim() !== ''
              }));
            }
            
            const fallbackSectionsWithContent = fallbackSectionsWithState
              .filter(section => section.hasContent)
              .map(section => section.key);
              
            if (fallbackSectionsWithContent.length > 0) {
              console.log(`[SectionSelectionModal] Found content in fallback language:`, fallbackSectionsWithContent);
              setAvailableSections(fallbackSectionsWithState);
              setSelectedSections(fallbackSectionsWithContent);
              
              // Show a toast to inform the user
              toast.info(t('researchDashboard.usingFallbackLanguage', { language: fallbackLanguage === 'ja' ? 'Japanese' : 'English' }));
            }
          }
        } catch (fallbackError) {
          console.error('[SectionSelectionModal] Fallback language also failed:', fallbackError);
        }
      }
      
    } catch (error) {
      console.error('Error fetching available sections:', error);
      toast.error(t('researchDashboard.aiMessages.fetchSectionsError', { message: error.message }));
    } finally {
      setIsFetchingSections(false);
    }
  };

  const getSectionTitle = (sectionKey) => {
    // Map backend keys to translation keys
    const translationKeyMapping = {
      'basic_information': 'basic',
      'business_structure': 'business_structure',
      'vision_analysis': 'vision',
      'management_strategy': 'management',
      'management_message': 'management_message',
      'crisis_management': 'crisis_management',
      'dx_analysis': 'dx',
      'financial_analysis': 'financial',
      'competitive_landscape': 'competitive',
      'regulatory_environment': 'regulatory',
      'salesforce_analysis': 'salesforce',
      'latest_news': 'news',
      'account_research': 'account_strategy'
    };

    const translationKey = translationKeyMapping[sectionKey] || sectionKey;
    return t(`researchDashboard.sections.${translationKey}`) || sectionKey;
  };

  const handleSectionToggle = (sectionKey) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionKey)) {
        return prev.filter(key => key !== sectionKey);
      } else {
        return [...prev, sectionKey];
      }
    });
  };

  const handleSelectAll = () => {
    const sectionsWithContent = availableSections
      .filter(section => section.hasContent)
      .map(section => section.key);
    setSelectedSections(sectionsWithContent);
  };

  const handleSelectNone = () => {
    setSelectedSections([]);
  };

  const handleDownload = async () => {
    if (selectedSections.length === 0) {
      toast.error(t('researchDashboard.selectAtLeastOneSection'));
      return;
    }

    setIsLoading(true);
    try {
      await onDownload(selectedSections);
      onClose();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false);
    }
  };

  const hasContentCount = availableSections.filter(section => section.hasContent).length;
  const selectedCount = selectedSections.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('researchDashboard.selectSectionsForPdf')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('researchDashboard.selectSectionsDescription')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isFetchingSections ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">{t('researchDashboard.loadingSections')}</span>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-blue-800">
                    <span className="font-medium">{hasContentCount}</span> {t('researchDashboard.sectionsAvailable')} • 
                    <span className="font-medium ml-1">{selectedCount}</span> {t('researchDashboard.sectionsSelected')}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      {t('researchDashboard.selectAll')}
                    </button>
                    <button
                      onClick={handleSelectNone}
                      className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      {t('researchDashboard.selectNone')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sections List */}
              <div className="space-y-2">
                {availableSections.map((section) => (
                  <div
                    key={section.key}
                    className={`flex items-center p-3 rounded-lg border transition-colors ${
                      section.hasContent 
                        ? 'bg-white border-gray-200 hover:border-blue-300' 
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <button
                      onClick={() => section.hasContent && handleSectionToggle(section.key)}
                      disabled={!section.hasContent}
                      className={`flex items-center justify-center w-5 h-5 rounded border transition-colors ${
                        selectedSections.includes(section.key)
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'border-gray-300 hover:border-blue-400'
                      } ${!section.hasContent ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {selectedSections.includes(section.key) && <Check size={12} />}
                    </button>
                    
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <span className={`font-medium ${
                          section.hasContent ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {section.title}
                        </span>
                        {!section.hasContent && (
                          <span className="ml-2 text-xs text-gray-400">
                            ({t('researchDashboard.noContent')})
                          </span>
                        )}
                      </div>
                                             {section.hasContent && section.content && (
                         <div className="text-xs text-gray-500 mt-1">
                           {section.content.length > 100 
                             ? `${section.content.substring(0, 100)}...` 
                             : section.content
                           }
                         </div>
                       )}
                       {section.hasContent && !section.content && (
                         <div className="text-xs text-gray-500 mt-1">
                           {t('researchDashboard.contentAvailable')}
                         </div>
                       )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Warning if no sections selected */}
              {selectedSections.length === 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle size={16} className="text-yellow-600 mr-2" />
                    <span className="text-sm text-yellow-800">
                      {t('researchDashboard.selectAtLeastOneSection')}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDownload}
            disabled={selectedSections.length === 0 || isLoading}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              selectedSections.length === 0 || isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('researchDashboard.generatingPdf')}
              </>
            ) : (
              <>
                <Download size={16} className="mr-2" />
                {t('researchDashboard.downloadPdf')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionSelectionModal; 